import { Heading, Table, TableBody, TableContainer, TableHead, TableHeader, TableRow } from '@ksyos/design-system';
import React, { useEffect, useState } from 'react';
import type { ServiceCode } from '@common/enums';
import type { CompensationPayoutForServiceActivityUpdatableFields } from '@common/validation/compensationPayoutForServiceActivity';
import type { PayableServiceActivity } from '@common/validation/payableServiceActivity';
import { ManagerAuthorization } from '@common/enums';
import { getLanguage } from '@common/translate/translate';
import { formatString } from '@common/utils/formatString';
import { isActiveOnDateOrInTheFuture } from '@common/utils/isActiveOnDate';
import { AddButtonHeader } from '@frontend/components/elements/AddButtonHeader';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { getServiceActivityCompensationPayouts } from '@frontend/services/api/compensationPayoutApiService';
import { getAllPayableActivities } from '@frontend/services/api/payableServiceActivityApiService';
import { hasOneAuthorization } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { PayableServiceActivityCompensationPayout } from './PayableServiceActivityCompensationPayout';

export function PayableServiceActivitiesList(props: Readonly<{ serviceCode: ServiceCode; serviceId: string }>) {
    const { serviceId, serviceCode } = props;
    const translate = getLanguage();
    const canEdit = hasOneAuthorization(ManagerAuthorization.serviceRightsCareproviders);
    const abortSignal = useAbortSignal();
    const [payableServiceActivities, setPayableServiceActivities] = useState<PayableServiceActivity[]>([]);
    const [serviceCompensationPayouts, setServiceCompensationPayouts] = useState<
        CompensationPayoutForServiceActivityUpdatableFields[]
    >([]);

    useEffect(() => {
        loadServiceCompensationPayouts();
    }, []);

    return (
        <PageTemplate width="full">
            <AddButtonHeader
                buttonText={formatString(translate.common.addItem, [translate.service.compensationPayout])}
                hideButton={!canEdit}
                link={`/services/${serviceId}/serviceCompensationPayouts/add`}
                style={{ marginBottom: 60, marginTop: 40 }}
                title={<Heading>Vergoedingen {serviceCode}</Heading>}
            />
            <TableContainer style={{ width: '95%' }}>
                <Table>
                    <React.Fragment>
                        <TableHead>
                            <TableRow>
                                <TableHeader>Activiteit</TableHeader>
                                <TableHeader>Afhankelijk van</TableHeader>
                                <TableHeader>Vergoeding</TableHeader>
                                <TableHeader>Begindatum</TableHeader>
                                <TableHeader>Einddatum</TableHeader>
                                <TableHeader>Uitbetalen aan</TableHeader>
                                <TableHeader></TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {payableServiceActivities.map((payableServiceActivity) => (
                                <PayableServiceActivityCompensationPayout
                                    canEdit={canEdit}
                                    key={payableServiceActivity.id}
                                    payableServiceActivity={payableServiceActivity}
                                    serviceCompensationPayouts={serviceCompensationPayouts}
                                    serviceId={serviceId}
                                />
                            ))}
                        </TableBody>
                    </React.Fragment>
                </Table>
            </TableContainer>
        </PageTemplate>
    );

    async function loadServiceCompensationPayouts() {
        const payableServiceActivitiesResult = await getAllPayableActivities({ serviceId }, abortSignal);
        const serviceCompensationPayoutsResult = await getServiceActivityCompensationPayouts(
            { serviceId },
            abortSignal
        );

        const sortedPayableServiceActivitiesServiceActivities = payableServiceActivitiesResult.sort(sortActiveFirst);
        setPayableServiceActivities(sortedPayableServiceActivitiesServiceActivities);
        setServiceCompensationPayouts(serviceCompensationPayoutsResult);
    }

    function sortActiveFirst(a: PayableServiceActivity, b: PayableServiceActivity) {
        const aIsActiveOnDateOrInTheFuture = isActiveOnDateOrInTheFuture(a, new Date());
        const bIsActiveOnDateOrInTheFuture = isActiveOnDateOrInTheFuture(b, new Date());

        if (!aIsActiveOnDateOrInTheFuture && bIsActiveOnDateOrInTheFuture) {
            return 1;
        }
        if (aIsActiveOnDateOrInTheFuture && !bIsActiveOnDateOrInTheFuture) {
            return -1;
        }

        return a.startDate < b.startDate ? 1 : -1;
    }
}
